import React, { useState } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { Phone, Mail, MapPin, Download, ChevronRight, Clock, Award, Users, Heart, MessageCircle, Instagram, Star, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast, Toaster } from "sonner";

const WHATSAPP_NUMBER = "573102760740";

function App() {
  const [formData, setFormData] = useState({
    nombre: "",
    telefono: "",
    mensaje: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const nombre = formData.nombre;
    const telefono = formData.telefono;
    const mensaje = formData.mensaje;

    const whatsappMessage = encodeURIComponent(
      `Hola, soy ${nombre}. Mi teléfono es ${telefono}. ${mensaje || 'Me gustaría más información sobre sus servicios.'}`
    );

    toast.success("¡Gracias! Abriendo WhatsApp...");
    setFormData({ nombre: "", telefono: "", mensaje: "" });

    setTimeout(() => {
      window.location.href = `https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${whatsappMessage}`;
      setIsSubmitting(false);
    }, 800);
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hola, me gustaría agendar una cita.");
    window.location.href = `https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${message}`;
  };

  const handleDownloadPortfolio = () => {
    window.open("https://customer-assets.emergentagent.com/job_dental-integral/artifacts/portafolio.pdf", "_blank");
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header 
        className="fixed top-0 w-full z-50 backdrop-blur-xl bg-white/70 border-b border-white/20"
        data-testid="header"
      >
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <img 
            src="https://customer-assets.emergentagent.com/job_dental-integral/artifacts/9nuivog4_logo%20oficial%20sin%20fondo.png" 
            alt="Dental AR - Odontología integral especializada" 
            className="w-[150px] h-auto block"
            style={{ background: 'transparent', border: 'none', boxShadow: 'none', padding: 0, margin: 0 }}
            data-testid="header-logo"
          />
          <nav className="hidden md:flex gap-8" data-testid="nav-menu">
            <button onClick={() => scrollToSection('inicio')} className="text-sm font-medium text-slate-700 hover:text-blue-700 transition-colors" data-testid="nav-inicio">Inicio</button>
            <button onClick={() => scrollToSection('nosotros')} className="text-sm font-medium text-slate-700 hover:text-blue-700 transition-colors" data-testid="nav-nosotros">Nosotros</button>
            <button onClick={() => scrollToSection('servicios')} className="text-sm font-medium text-slate-700 hover:text-blue-700 transition-colors" data-testid="nav-servicios">Servicios</button>
            <button onClick={() => scrollToSection('contacto')} className="text-sm font-medium text-slate-700 hover:text-blue-700 transition-colors" data-testid="nav-contacto">Contacto</button>
          </nav>
          <Button 
            onClick={handleWhatsAppClick}
            className="bg-blue-700 hover:bg-blue-800 text-white rounded-full px-6"
            data-testid="header-cta-button"
          >
            Agendar Cita
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        id="inicio"
        className="min-h-[90vh] pt-36 px-6" 
        data-testid="hero-section"
      >
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-start py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="inline-block mb-4"
            >
              <span className="text-sm font-bold tracking-[0.2em] uppercase text-blue-700 bg-blue-50 px-4 py-2 rounded-full">
                +20 años creando sonrisas
              </span>
            </motion.div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter text-slate-900 mb-6" data-testid="hero-title">
              ¡El cuidado de tu sonrisa <span className="text-blue-700 font-medium">está en nuestras manos!</span>
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-slate-600 mb-8" data-testid="hero-subtitle">
              Expertos en cuidar tu salud y bienestar bucal. Laboratorio y consultorio integrado en un solo lugar. 
            <strong className="block text-amber-500 mt-2">
               ¡ Agenda Tu valoración completamente GRATIS !
            </strong>
            </p>
            <motion.div 
              className="flex flex-wrap gap-4 mb-8"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Button 
                onClick={handleWhatsAppClick}
                className="bg-blue-700 hover:bg-blue-800 text-white rounded-full px-8 py-6 text-lg hover:-translate-y-1 hover:shadow-lg hover:shadow-blue-300 transition-all duration-300"
                data-testid="hero-cta-button"
              >
                Agenda tu valoracion gratis aqui <ChevronRight className="ml-2" />
              </Button>
              <Button 
                onClick={() => scrollToSection('servicios')}
                variant="outline"
                className="rounded-full px-8 py-6 text-lg border-2 border-blue-700 text-blue-700 hover:bg-blue-50 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                data-testid="hero-services-button"
              >
                Nuestros Servicios
              </Button>
            </motion.div>

            {/* Inline Appointment Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border border-slate-200 shadow-lg"
              data-testid="hero-appointment-form"
            >
              <h3 className="text-lg font-medium text-slate-900 mb-4">Agenda tu cita gratis</h3>
              <form onSubmit={handleSubmit} className="space-y-3" data-testid="contact-form">
                <Input
                  type="text"
                  placeholder="Tu nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  required
                  className="bg-white border-slate-200 focus:border-blue-700 focus:ring-1 focus:ring-blue-700"
                  data-testid="contact-form-name-input"
                />
                <Input
                  type="tel"
                  placeholder="Tu teléfono"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  required
                  className="bg-white border-slate-200 focus:border-blue-700 focus:ring-1 focus:ring-blue-700"
                  data-testid="contact-form-phone-input"
                />
                <Textarea
                  placeholder="¿En qué podemos ayudarte? (opcional)"
                  value={formData.mensaje}
                  onChange={(e) => setFormData({ ...formData, mensaje: e.target.value })}
                  rows={2}
                  className="bg-white border-slate-200 focus:border-blue-700 focus:ring-1 focus:ring-blue-700"
                  data-testid="contact-form-message-input"
                />
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-full py-5 font-medium hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                  data-testid="contact-form-submit-button"
                >
                  {isSubmitting ? "Enviando..." : "Enviar y contactar por WhatsApp"}
                </Button>
              </form>
            </motion.div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <motion.div 
              className="rounded-3xl overflow-hidden shadow-2xl shadow-blue-200" 
              data-testid="hero-video"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <video 
                autoPlay 
                loop 
                muted 
                playsInline
                className="w-full h-auto"
              >
                <source src="https://customer-assets.emergentagent.com/job_dental-integral/artifacts/yf4vzpc6_WhatsApp%20Video%202026-03-29%20at%209.17.03%20PM.mp4" type="video/mp4" />
              </video>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Nuestro Personal Section */}
      <section
        id="nosotros"
        className="py-24 px-6 bg-slate-50"
        data-testid="personal-section"
      >
        <div className="max-w-5xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <span className="text-sm font-bold tracking-[0.2em] uppercase text-blue-700 mb-4 block">Nuestro Personal</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 mb-4">
              El equipo detrás de tu sonrisa
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-slate-600 max-w-2xl mx-auto">
              Profesionales comprometidos con tu salud bucal y bienestar
            </p>
          </motion.div>

          {/* Tarjeta — Personal del consultorio */}
          <motion.div
            className="flex flex-col md:flex-row gap-10 items-center bg-white rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 p-8 border border-transparent hover:border-blue-100"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <motion.div
              className="w-full md:w-2/5 flex-shrink-0"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <img
                src="/fotos pagina dentalar/imagen dientes.jpg"
                alt="Personal odontológico Dental AR"
                className="rounded-3xl shadow-xl w-full object-cover"
              />
            </motion.div>
            <div className="flex-1">
              <span className="text-sm font-bold tracking-[0.2em] uppercase text-blue-700 mb-3 block">Quiénes Somos</span>
              <h3 className="text-2xl sm:text-3xl font-medium text-slate-900 mb-4">Su laboratorio de confianza</h3>
              <p className="text-base leading-relaxed text-slate-600 mb-4">
                Somos una clinica odontologica con laboratorio dental y consultorio integrado en un solo lugar. Todo tu tratamiento se realiza aquí, sin intermediarios, con calidad garantizada y tiempos de entrega más rápidos.
              </p>
              <p className="text-base leading-relaxed text-slate-600 mb-6">
                En Dental AR buscamos crear más allá de un vínculo comercial, un vínculo de amistad con nuestros clientes. 
              <strong className="block text-amber-500 mt-2">
              ¡ Agenda tu valoracion GRATIS !
              </strong>
              </p>
              <div className="grid grid-cols-3 gap-4">
                <motion.div className="text-center" whileHover={{ scale: 1.1 }} transition={{ duration: 0.2 }}>
                  <div className="text-3xl font-bold text-blue-700">20+</div>
                  <div className="text-sm text-slate-600">Años</div>
                </motion.div>
                <motion.div className="text-center" whileHover={{ scale: 1.1 }} transition={{ duration: 0.2 }}>
                  <div className="text-3xl font-bold text-blue-700">5000+</div>
                  <div className="text-sm text-slate-600">Pacientes</div>
                </motion.div>
                <motion.div className="text-center" whileHover={{ scale: 1.1 }} transition={{ duration: 0.2 }}>
                  <div className="text-3xl font-bold text-blue-700">98%</div>
                  <div className="text-sm text-slate-600">Satisfacción</div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section 
        id="servicios"
        className="py-24 px-6 bg-slate-50" 
        data-testid="services-section"
      >
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm font-bold tracking-[0.2em] uppercase text-blue-700 mb-4 block">Nuestros Servicios</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 mb-4">
              Cuidado dental integral
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-slate-600 max-w-2xl mx-auto">
              Ofrecemos una amplia gama de servicios especializados para todas tus necesidades dentales
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Odontología General",
                description: "Profilaxis, limpieza periodontal, detartraje, resinas, fluorización y sellantes.",
                image: "/fotos pagina dentalar/odontologia.jpg",
                testid: "service-card-general"
              },
              {
                title: "Ortodoncia",
                description: "Brackets metálicos, técnica MBT, autoligado y retenedores personalizados.",
                image: "https://images.pexels.com/photos/6528861/pexels-photo-6528861.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
                testid: "service-card-orthodontics"
              },
              {
                title: "Blanqueamiento",
                description: "3 sesiones profesionales con limpieza inicial y final para una sonrisa radiante.",
                image: "https://images.pexels.com/photos/6627570/pexels-photo-6627570.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
                testid: "service-card-whitening"
              },
              {
                title: "Implantes Dentales",
                description: "Implantes marca ADIN con corona en zirconio de última generación.",
                image: "https://images.unsplash.com/photo-1771442873035-474765b40ac6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxkZW50YWwlMjBpbXBsYW50JTIwcHJvY2VkdXJlfGVufDB8fHx8MTc3NjYyNTY2Mnww&ixlib=rb-4.1.0&q=85",
                testid: "service-card-implants"
              },
              {
                title: "Estética Dental",
                description: "Carillas en resina, porcelana, disilicato y coronas en zirconio.",
                image: "/fotos pagina dentalar/Diseño de sonrisa.jpeg",
                testid: "service-card-aesthetic"
              },
              {
                title: "Prótesis Dentales",
                description: "Prótesis fijas, removibles, parciales flexibles y totales acrílicas.",
                image: "https://images.pexels.com/photos/6502305/pexels-photo-6502305.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
                testid: "service-card-prosthetics"
              }
            ].map((service, index) => (
              <motion.div
                key={service.testid}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-transparent hover:border-blue-200 flex flex-col"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                data-testid={service.testid}
              >
                <div className="h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title} 
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="text-lg font-medium text-slate-900 mb-2">{service.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{service.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 px-6" data-testid="testimonials-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm font-bold tracking-[0.2em] uppercase text-blue-700 mb-4 block">Testimonios</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 mb-4">
              Lo que dicen nuestros pacientes
            </h2>
          </div>

          {(() => {
            const testimonials = [
              {
                name: "Carolina Méndez",
                treatment: " Diseño de Sonrisa ",
                quote: "Después de mi diseño de sonrisa en Dental AR, no paro de sonreír. la odontologa Catalina fue muy profesional y el resultado superó mis expectativas.",
                video: "https://customer-assets.emergentagent.com/job_dental-integral/artifacts/e5ii5xhd_testimonio%20dientes.mp4",
                rating: 5
              },
              
            ];
            const t = testimonials[currentTestimonial];
            return (
              <div className="relative max-w-3xl mx-auto">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentTestimonial}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.4 }}
                    className="bg-white rounded-3xl overflow-hidden shadow-lg border border-slate-100"
                  >
                    <div className="grid md:grid-cols-2">
                      <div className="relative h-64 md:h-auto md:min-h-[350px] overflow-hidden">
                        {t.video ? (
                          <video autoPlay loop muted playsInline className="w-full h-full object-cover">
                            <source src={t.video} type="video/mp4" />
                          </video>
                        ) : (
                          <img src={t.image} alt={t.name} className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="p-8 md:p-10 flex flex-col justify-center">
                        <div className="flex gap-1 mb-4">
                          {[...Array(t.rating)].map((_, i) => (
                            <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                          ))}
                        </div>
                        <Quote className="w-10 h-10 text-blue-100 mb-4" />
                        <p className="text-base text-slate-600 leading-relaxed italic mb-6">"{t.quote}"</p>
                        <div className="border-t border-slate-100 pt-4">
                          <div className="font-semibold text-slate-900 text-lg">{t.name}</div>
                          <div className="text-sm text-blue-600 font-medium">{t.treatment}</div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
                <div className="flex items-center justify-center gap-6 mt-8">
                  <motion.button
                    onClick={() => setCurrentTestimonial((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1))}
                    className="w-12 h-12 rounded-full bg-white border-2 border-blue-700 text-blue-700 flex items-center justify-center hover:bg-blue-700 hover:text-white transition-all duration-300 shadow-md"
                    whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}
                  >
                    <ChevronRight className="w-5 h-5 rotate-180" />
                  </motion.button>
                  <div className="flex gap-2">
                    {testimonials.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentTestimonial(idx)}
                        className={`w-3 h-3 rounded-full transition-all duration-300 ${idx === currentTestimonial ? 'bg-blue-700 w-8' : 'bg-slate-300 hover:bg-slate-400'}`}
                      />
                    ))}
                  </div>
                  <motion.button
                    onClick={() => setCurrentTestimonial((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1))}
                    className="w-12 h-12 rounded-full bg-white border-2 border-blue-700 text-blue-700 flex items-center justify-center hover:bg-blue-700 hover:text-white transition-all duration-300 shadow-md"
                    whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </motion.button>
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* Mission and Vision Section */}
      <section className="py-24 px-6" data-testid="mission-vision-section">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-0">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-blue-50 rounded-l-3xl p-12"
            whileHover={{ scale: 1.02 }}
          >
            <span className="text-sm font-bold tracking-[0.2em] uppercase text-blue-700 mb-4 block">Nuestra Misión</span>
            <h3 className="text-3xl font-medium text-slate-900 mb-6">Excelencia en rehabilitación oral</h3>
            <p className="text-base md:text-lg leading-relaxed text-slate-600">
              Ser reconocidos como el mejor grupo especializado en el tratamiento para la rehabilitación oral, brindando atención a los detalles de cada trabajo con profesionalismo.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-white rounded-r-3xl p-12 border border-slate-100"
            whileHover={{ scale: 1.02 }}
          >
            <span className="text-sm font-bold tracking-[0.2em] uppercase text-blue-700 mb-4 block">Nuestra Visión</span>
            <h3 className="text-3xl font-medium text-slate-900 mb-6">Líderes en el sector 2026</h3>
            <p className="text-base md:text-lg leading-relaxed text-slate-600">
              Liderar el sector con una línea de productos confiables, siendo el proveedor preferido de nuestros clientes, proyectados hacia el futuro tecnológicamente eficaz.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-6" data-testid="faq-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm font-bold tracking-[0.2em] uppercase text-blue-700 mb-4 block">Preguntas Frecuentes</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 mb-4">Resolvemos tus dudas</h2>
          </div>
          <div className="space-y-4">
            {[
              { question: "¿Por qué elegir implantes de zirconio?", answer: "El zirconio es un material biocompatible, extremadamente resistente y estéticamente superior. No produce reacciones alérgicas y ofrece un aspecto natural idéntico a tus dientes originales." },
              { question: "¿La valoración tiene algún costo?", answer: "No, la valoración en Dental AR es completamente gratuita. Evaluamos tu caso, te explicamos las opciones y te damos un presupuesto sin compromiso." },
              { question: "¿Qué ventaja tiene tener laboratorio y consultorio en el mismo lugar?", answer: "Eliminamos intermediarios, reducimos tiempos de entrega, garantizamos mejor calidad y ofrecemos precios más competitivos." },
              { question: "¿Cuánto dura un tratamiento de ortodoncia?", answer: "Generalmente entre 12 y 24 meses. En tu valoración gratuita te damos un estimado preciso." },
              { question: "¿Ofrecen opciones de financiamiento?", answer: "Sí, aceptamos todas las tarjetas débito y crédito, además de Nequi y Daviplata." },
              { question: "¿Cuántas sesiones necesita un blanqueamiento dental?", answer: "Nuestro tratamiento incluye 3 sesiones profesionales más profilaxis inicial y final." }
            ].map((faq, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl border border-slate-100 overflow-hidden"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                <details className="group">
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                    <h3 className="text-base font-medium text-slate-900 pr-4">{faq.question}</h3>
                    <ChevronRight className="w-5 h-5 text-blue-700 flex-shrink-0 transition-transform duration-300 group-open:rotate-90" />
                  </summary>
                  <div className="px-6 pb-6 pt-0">
                    <p className="text-sm text-slate-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

{/* Contact Section */}
<section 
  id="contacto"
  className="py-24 px-6 bg-slate-50" 
  data-testid="contact-section"
>
  <div className="max-w-5xl mx-auto">
    
    <div className="text-center mb-16">
      <span className="text-sm font-bold tracking-[0.2em] uppercase text-blue-700 mb-4 block">
        Contacto
      </span>

      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 mb-4">
        Agenda tu cita hoy
      </h2>

      <p className="text-base md:text-lg leading-relaxed text-slate-600 max-w-2xl mx-auto">
        Estamos listos para atenderte y brindarte la mejor experiencia dental
      </p>
    </div>

    <div className="grid md:grid-cols-2 gap-16">

      {/* FORMULARIO */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <form onSubmit={handleSubmit} className="space-y-6">

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Nombre completo
            </label>

            <Input
              type="text"
              placeholder="Tu nombre"
              value={formData.nombre}
              onChange={(e) =>
                setFormData({ ...formData, nombre: e.target.value })
              }
              required
              className="bg-slate-50 border-slate-200 focus:border-blue-700"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Teléfono
            </label>

            <Input
              type="tel"
              placeholder="Tu teléfono"
              value={formData.telefono}
              onChange={(e) =>
                setFormData({ ...formData, telefono: e.target.value })
              }
              required
              className="bg-slate-50 border-slate-200 focus:border-blue-700"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Mensaje (opcional)
            </label>

            <Textarea
              placeholder="¿En qué podemos ayudarte?"
              value={formData.mensaje}
              onChange={(e) =>
                setFormData({ ...formData, mensaje: e.target.value })
              }
              rows={4}
              className="bg-slate-50 border-slate-200 focus:border-blue-700"
            />
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-full py-6 text-lg font-medium"
          >
            {isSubmitting
              ? "Enviando..."
              : "Enviar y contactar por WhatsApp"}
          </Button>
        </form>
      </motion.div>

      {/* INFORMACIÓN */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        viewport={{ once: true }}
        className="space-y-8"
      >

        {/* WHATSAPP */}
        <motion.div
          className="bg-green-50 rounded-2xl p-8 border-2 border-green-200 cursor-pointer hover:shadow-xl transition-all duration-300"
          onClick={handleWhatsAppClick}
          whileHover={{ scale: 1.02, y: -5 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="mb-4">
            <img
              src="/fotos pagina dentalar/whatsapp.png"
              alt="WhatsApp"
              className="w-12 h-12 object-contain"
            />
          </div>

          <h3 className="text-2xl font-medium text-slate-900 mb-3">
            Habla con nosotros directamente
          </h3>

          <p className="text-slate-600 leading-relaxed mb-6">
            Contacta con nuestro equipo por WhatsApp y recibe respuesta inmediata.
          </p>

          <Button className="bg-green-500 hover:bg-green-600 text-white rounded-full px-6">
            Abrir WhatsApp
          </Button>
        </motion.div>

        {/* INFORMACIÓN DE CONTACTO */}
        <div className="bg-white rounded-2xl p-8 shadow-sm space-y-6">

          <motion.div
            className="flex items-start gap-4"
            whileHover={{ x: 5 }}
          >
            <Phone className="w-6 h-6 text-blue-700 flex-shrink-0 mt-1" />

            <div>
              <div className="font-medium text-slate-900">
                Teléfonos
              </div>

              <div className="text-slate-600">
                +57 310 310 4366
              </div>

              <div className="text-slate-600">
                +57 310 276 0740
              </div>
            </div>
          </motion.div>

          <motion.div
            className="flex items-start gap-4"
            whileHover={{ x: 5 }}
          >
            <Mail className="w-6 h-6 text-blue-700 flex-shrink-0 mt-1" />

            <div>
              <div className="font-medium text-slate-900">
                Email
              </div>

              <div className="text-slate-600">
                arleylab-423@gmail.com
              </div>
            </div>
          </motion.div>

          <motion.div
            className="flex items-start gap-4"
            whileHover={{ x: 5 }}
          >
            <MapPin className="w-6 h-6 text-blue-700 flex-shrink-0 mt-1" />

            <div>
              <div className="font-medium text-slate-900">
                Ubicación
              </div>

              <div className="text-slate-600">
                Carrera 78 #7b-38, Barrio Castilla, Kennedy, Bogotá
              </div>
            </div>
          </motion.div>

          <motion.div
            className="flex items-start gap-4"
            whileHover={{ x: 5 }}
          >
            <Clock className="w-6 h-6 text-blue-700 flex-shrink-0 mt-1" />

            <div>
              <div className="font-medium text-slate-900">
                Horario
              </div>

              <div className="text-slate-600">
                Lun - Vie: 8:00 AM - 6:00 PM
              </div>

              <div className="text-slate-600">
                Sábado: 9:00 AM - 2:00 PM
              </div>
            </div>
          </motion.div>

          <motion.div
            className="flex items-start gap-4"
            whileHover={{ x: 5 }}
          >
            <Instagram className="w-6 h-6 text-blue-700 flex-shrink-0 mt-1" />

            <div>
              <div className="font-medium text-slate-900">
                Instagram
              </div>

              <a
                href="https://www.instagram.com/odontologia_dentalar?igsh=bGtvNmJob3ZreWln"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 transition-colors"
              >
                @odontologia_dentalar
              </a>
            </div>
          </motion.div>

        </div>
      </motion.div>
    </div>
  </div>

  {/* SECCIÓN VISÍTANOS */}
  <div className="max-w-7xl mx-auto mt-24">

    <motion.div
      className="bg-white rounded-3xl overflow-hidden shadow-2xl"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7 }}
      viewport={{ once: true }}
    >

      <div className="bg-blue-700 text-white px-8 py-10 text-center">

        <span className="uppercase tracking-[0.2em] text-sm font-semibold text-white/80">
          Visítanos Barrio Castilla , localidad Kennedy 
        </span>

        <h2 className="text-3xl md:text-5xl font-semibold mt-3 mb-4">
          Conoce nuestras instalaciones
        </h2>

        <p className="text-white/90 max-w-3xl mx-auto text-base md:text-lg leading-relaxed">
          Un espacio moderno, cómodo y diseñado para brindarte la mejor atención odontológica en Bogotá.
        </p>

      </div>

      <img
        src="/fotos pagina dentalar/sede fisica.jpg"
        alt="Sede física Dental AR - Castilla, Kennedy, Bogotá"
        className="w-full object-cover"
        style={{ maxHeight: "750px" }}
      />

    </motion.div>
  </div>
</section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-6">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col">
            <span className="text-lg font-bold tracking-tight text-white">Dental AR</span>
            <span className="text-xs font-medium text-slate-400 tracking-wide">ARLEY MAHECHA</span>
          </div>
          <p className="text-slate-400 text-sm">© {new Date().getFullYear()} Dental AR. Todos los derechos reservados.</p>
        </div>
      </footer>

            {/* WhatsApp FAB */}
          <motion.button
        onClick={handleWhatsAppClick}
        className="fixed bottom-6 right-6 z-50 rounded-full w-16 h-16 bg-green-500 text-white flex items-center justify-center shadow-2xl overflow-hidden"
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 1, type: "spring", stiffness: 260, damping: 20 }}
        whileHover={{ scale: 1.15 }}
        whileTap={{ scale: 0.95 }}
      >
        <img
          src="/fotos pagina dentalar/whatsapp.png"
          alt="WhatsApp"
          className="w-10 h-10 object-contain"
        />
      </motion.button>
    </div>
  );
}

export default App;